import { useCallback } from 'react';
import ViewResolver from '@Components/ViewResolver/ViewResolver';

import { useAppDispatch } from '@Hooks/index';
import { modalActions } from '@Store/slices/modalSlice';
import { ManageRequestList } from './desktop';
import { RequestListMobile } from './mobile';
import { ManageRequestListProps } from './desktop/ManageRequestList';
import { RequestListMobileProps } from './mobile/RequestListMobile';

interface TenderRequestListProps
  extends Omit<
      ManageRequestListProps,
      'onVendors' | 'onUpdate' | 'onExtention' | 'onView' | 'onInitiate'
    >,
    Omit<
      RequestListMobileProps,
      | 'onVendors'
      | 'onUpdate'
      | 'onExtention'
      | 'onView'
      | 'onCancel'
      | 'onInitiate'
    > {}

const TenderRequestList: React.FC<TenderRequestListProps> = (
  props
): JSX.Element | null => {
  const {
    onComparison,
    onPrevious,
    onApprovalHistory,
    onCancel,
    onDelete,
    onLoadMore,
    listRequests,
    requestsDataList,
    onChangePage,
    mapColumns,
    dataSource,
    onSort,
    requestType,
    currentPage,
    showAction,
    hidePagination = false,
    isFiltersApplied,
    isSearchApplied,
    loading,
  } = props;
  const dispatch = useAppDispatch();

  const handleViewRecord = useCallback(
    (recordKey: string): void => {
      dispatch(
        modalActions.setStatusModal({
          status: true,
          key: String(recordKey),
          type: 'TenderView',
        })
      );
    },
    [dispatch]
  );

  const handleVendorsList = useCallback(
    (recordKey: string): void => {
      dispatch(
        modalActions.setStatusModal({
          status: true,
          key: String(recordKey),
          type: 'TenderVendorList',
        })
      );
    },
    [dispatch]
  );

  const handleUpdateTenderMode = useCallback(
    (recordKey: string): void => {
      dispatch(
        modalActions.setStatusModal({
          status: true,
          key: String(recordKey),
          type: 'UpdateTenderMode',
        })
      );
    },
    [dispatch]
  );

  const handleTenderExtentionUpdate = useCallback(
    (recordKey: string): void => {
      dispatch(
        modalActions.setStatusModal({
          status: true,
          key: String(recordKey),
          type: 'UpdateTenderExtension',
        })
      );
    },
    [dispatch]
  );

  const handleOpenInitiateRfqModal = useCallback(
    (recordKey: string, prFor: string): void => {
      dispatch(
        modalActions.setStatusModal({
          key: String(recordKey),
          status: true,
          type: 'InitiateRFQ',
          params: { prFor },
        })
      );
    },
    [dispatch]
  );

  if (!listRequests) {
    return null;
  }

  return (
    <ViewResolver
      desktop={
        <ManageRequestList
          showAction={showAction}
          onView={handleViewRecord}
          onVendors={handleVendorsList}
          onComparison={onComparison}
          onPrevious={onPrevious}
          onUpdate={handleUpdateTenderMode}
          onInitiate={handleOpenInitiateRfqModal}
          onCancel={onCancel}
          onDelete={onDelete}
          onExtention={handleTenderExtentionUpdate}
          onApprovalHistory={onApprovalHistory}
          totalItems={listRequests.totalElements}
          pageSize={listRequests.size}
          onChangePage={onChangePage}
          currentPage={currentPage}
          mapColumns={mapColumns}
          dataSource={dataSource}
          onSort={onSort}
          hidePagination={hidePagination}
          isFiltersApplied={isFiltersApplied}
          isSearchApplied={isSearchApplied}
          loading={loading}
        />
      }
      mobile={
        <RequestListMobile
          listRequests={listRequests}
          requestsDataList={requestsDataList}
          requestType={requestType}
          showAction={showAction}
          onLoadMore={onLoadMore}
          onView={handleViewRecord}
          onVendors={handleVendorsList}
          onComparison={onComparison}
          onPrevious={onPrevious}
          onUpdate={handleUpdateTenderMode}
          onExtention={handleTenderExtentionUpdate}
          onCancel={onCancel}
          onDelete={onDelete}
          onInitiate={handleOpenInitiateRfqModal}
          onApprovalHistory={onApprovalHistory}
          isFiltersApplied={isFiltersApplied}
          isSearchApplied={isSearchApplied}
        />
      }
    />
  );
};

export default TenderRequestList;
